import {
  fibreOfferField,
  fieldProvinceVUL,
  CONSTANT
} from '../../../utils/constant';
import { fibreSpeed } from '../../vulcanPricing/fibeInternet/fibreInternetFieldNames';

const { CORP_STORE } = CONSTANT.KEYS.CHANNELS;
const QUEBEC_PROVINCES = ['quebec', 'québec', 'qc', 'pq'];
const ONTARIO_PROVINCES = ['ontario', 'on', 'ont'];
const inList = (addr, list) => list.includes((addr || '').toLowerCase());

/**
 * Pure rewrite of the fibre-OFFER matrix in `FibreBusinessWifiHelpers` — filters
 * the `offers` field by fibre speed + province (Quebec / Ontario / rest), removes
 * ATL offers for the Corp Store channel, then narrows to ATL-only / BTL-only /
 * both based on the label of the most recently selected offer. Arrays transcribed
 * verbatim from the helper; verified by rules/fibreOffers.diff.test.js.
 *
 * @param {{channelName?: string, t: Object}} ctx
 * @returns {import('../vulcanFieldRules').VulcanRule}
 */
export function makeFibreOffersRule(ctx) {
  const { channelName, t } = ctx;
  const {
    vulThreeG,
    vulOnePointFiveG,
    vulOneG,
    vulATLOption,
    vulBTLOption,
    vulFifteenDollarInternetBundleDiscount,
    vulHundredBillCreditHSIA,
    vulSeventyDollarInternetDiscount,
    vulTenDollarInternetBundleDiscount,
    vulFortyFiveDollarInternetDiscountATL,
    vulFifteenDollarInternetBundleDiscountATL,
    vulTenDollarWifiBundleDiscount,
    vulSeventyFiveDollarInternetDiscount,
    vulWifiTenDollarDiscountAtlBtl,
    vulSixtyDollarInternetDiscountATL,
    vulOneDollarInternetBundleDiscount,
    vulFiftyDollarInternetDiscount,
    vulThirtyFiveDollarInternetDiscount,
    vulHundredDollarBillCreditAtlBtl,
    vulThirtyDollarInternetDiscount
  } = t;

  const threeGOffers = [
    vulFifteenDollarInternetBundleDiscount,
    vulHundredBillCreditHSIA,
    vulSeventyDollarInternetDiscount
  ];
  const onePointFiveGOffers = [
    vulTenDollarInternetBundleDiscount,
    vulFortyFiveDollarInternetDiscountATL,
    vulSeventyDollarInternetDiscount,
    vulHundredBillCreditHSIA,
    vulFifteenDollarInternetBundleDiscount
  ];
  const threeGOffersForQuebec = [
    vulFifteenDollarInternetBundleDiscountATL,
    vulTenDollarWifiBundleDiscount,
    vulSeventyFiveDollarInternetDiscount,
    vulHundredBillCreditHSIA,
    vulWifiTenDollarDiscountAtlBtl
  ];
  const onePointFiveGOffersForQuebec = [
    vulFifteenDollarInternetBundleDiscount,
    vulTenDollarInternetBundleDiscount,
    vulSixtyDollarInternetDiscountATL,
    vulSeventyDollarInternetDiscount,
    vulHundredBillCreditHSIA,
    vulTenDollarWifiBundleDiscount,
    vulOneDollarInternetBundleDiscount,
    vulWifiTenDollarDiscountAtlBtl,
    vulFiftyDollarInternetDiscount
  ];
  const onePointFiveGOffersForOntario = [
    vulFifteenDollarInternetBundleDiscount,
    vulTenDollarInternetBundleDiscount,
    vulSeventyDollarInternetDiscount,
    vulHundredBillCreditHSIA,
    vulTenDollarWifiBundleDiscount,
    vulOneDollarInternetBundleDiscount,
    vulWifiTenDollarDiscountAtlBtl,
    vulThirtyFiveDollarInternetDiscount
  ];
  const threeGOffersForOntario = [
    vulFifteenDollarInternetBundleDiscountATL,
    vulTenDollarWifiBundleDiscount,
    vulSeventyFiveDollarInternetDiscount,
    vulHundredBillCreditHSIA,
    vulWifiTenDollarDiscountAtlBtl
  ];
  const oneGOffers = [
    vulWifiTenDollarDiscountAtlBtl,
    vulHundredDollarBillCreditAtlBtl,
    vulSeventyDollarInternetDiscount,
    vulFortyFiveDollarInternetDiscountATL,
    vulThirtyDollarInternetDiscount
  ];
  const oneGOffersForQuebec = [
    vulWifiTenDollarDiscountAtlBtl,
    vulHundredDollarBillCreditAtlBtl,
    vulSeventyDollarInternetDiscount,
    vulFortyFiveDollarInternetDiscountATL
  ];
  const oneGOffersForOntario = [
    vulWifiTenDollarDiscountAtlBtl,
    vulHundredDollarBillCreditAtlBtl,
    vulSeventyDollarInternetDiscount,
    vulThirtyDollarInternetDiscount
  ];

  const pickBySpeed = (speed, quebec, ontario, rest) => {
    if (inList(quebec.province, QUEBEC_PROVINCES)) return quebec.list;
    if (inList(ontario.province, ONTARIO_PROVINCES)) return ontario.list;
    return rest;
  };

  return (field, state, values) => {
    if (field.name !== fibreOfferField) return undefined;
    const baseOptions = state.options || field.options || [];
    const speed = values[fibreSpeed];
    const province = values[fieldProvinceVUL];

    let allow;
    if (speed === vulThreeG) {
      allow = pickBySpeed(
        speed,
        { province, list: threeGOffersForQuebec },
        { province, list: threeGOffersForOntario },
        threeGOffers
      );
    } else if (speed === vulOnePointFiveG) {
      allow = pickBySpeed(
        speed,
        { province, list: onePointFiveGOffersForQuebec },
        { province, list: onePointFiveGOffersForOntario },
        onePointFiveGOffers
      );
    } else if (speed === vulOneG) {
      allow = pickBySpeed(
        speed,
        { province, list: oneGOffersForQuebec },
        { province, list: oneGOffersForOntario },
        oneGOffers
      );
    }

    let valid = allow
      ? baseOptions.filter(o => allow.includes(o.fields.value))
      : baseOptions;

    // Corp Store: remove ATL-only offers.
    if (channelName === CORP_STORE) {
      valid = valid.filter(o => {
        const v = o.fields.value;
        return (
          v !== vulTenDollarInternetBundleDiscount &&
          v !== vulSixtyDollarInternetDiscountATL &&
          v !== vulFortyFiveDollarInternetDiscountATL &&
          v !== vulFifteenDollarInternetBundleDiscountATL
        );
      });
    }

    // Narrow by the label of the most recently selected offer (ATL/BTL).
    const selected = values[fibreOfferField];
    if (Array.isArray(selected) && selected.length > 0) {
      const last = selected[selected.length - 1];
      const selectedOption = valid.find(o => o.fields.value === last);
      if (selectedOption) {
        const label = selectedOption.fields.label.toLowerCase().trim();
        const atl = vulATLOption.toLowerCase().trim();
        const btl = vulBTLOption.toLowerCase().trim();
        const isAtl = label.includes(atl);
        const isBtl = label.includes(btl);
        if (isAtl && !isBtl) {
          valid = valid.filter(o =>
            o.fields.label
              .toLowerCase()
              .trim()
              .includes(atl)
          );
        } else if (isBtl && !isAtl) {
          valid = valid.filter(o =>
            o.fields.label
              .toLowerCase()
              .trim()
              .includes(btl)
          );
        }
        // both / neither → keep `valid` as-is
      }
    }

    return { options: valid };
  };
}

export default makeFibreOffersRule;
